import React, { useState, useEffect } from 'react';
import { Cpu, Clock, Zap, TrendingUp, Wifi, ShieldCheck, ShieldAlert, Activity, HardDrive, ArrowDown, ArrowUp } from 'lucide-react';
import { exchangeRateAPI } from '../services/api';

const REGION_OPTIONS = [
  { value: 'any', label: 'Anywhere in the world' },
  { value: 'EU', label: 'Europe' },
  { value: 'NA', label: 'North America' },
  { value: 'NA-EAST', label: 'NA — East' },
  { value: 'NA-WEST', label: 'NA — West' },
  { value: 'ASIA', label: 'Asia' },
  { value: 'OCEANIA', label: 'Oceania' },
  { value: 'ME', label: 'Middle East' },
  { value: 'SA', label: 'South America' },
];

const FILTER_SELECT_CLASS =
  'w-full appearance-none bg-slate-950 border border-indigo-400/45 text-white text-sm font-medium rounded-xl px-3.5 py-2.5 pr-10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] hover:border-indigo-300/70 focus:outline-none focus:ring-2 focus:ring-indigo-400/70 focus:border-indigo-300';

const FilterSelect = ({ label, value, onChange, children }) => (
  <div className="flex-1 min-w-[10rem]">
    <label className="block text-xs font-medium text-indigo-200/80 mb-1.5">{label}</label>
    <div className="relative">
      <select value={value} onChange={onChange} className={FILTER_SELECT_CLASS}>
        {children}
      </select>
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-indigo-300 text-xs">▾</span>
    </div>
  </div>
);

const SORT_OPTIONS = [
  { value: 'price', label: 'Cheapest' },
  { value: 'latency', label: 'Lowest ping' },
  { value: 'value', label: 'Best value (price + ping)' },
];

// Renders a two-letter code as an emoji flag, e.g. "DE" -> 🇩🇪. Falls back to
// the code itself when we don't have a valid ISO alpha-2 pair.
const flagFromCountryCode = (code) => {
  if (!code || code.length !== 2) return '';
  const A = 0x1f1e6;
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(A + cc.charCodeAt(0) - 65) + String.fromCodePoint(A + cc.charCodeAt(1) - 65);
};

const pingColor = (ms) => {
  if (ms == null) return { text: 'text-gray-400', bg: 'bg-gray-700/50', border: 'border-gray-600' };
  if (ms < 60)  return { text: 'text-green-300',  bg: 'bg-green-900/30', border: 'border-green-600/40' };
  if (ms < 120) return { text: 'text-yellow-300', bg: 'bg-yellow-900/30', border: 'border-yellow-600/40' };
  if (ms < 200) return { text: 'text-orange-300', bg: 'bg-orange-900/30', border: 'border-orange-600/40' };
  return { text: 'text-red-300', bg: 'bg-red-900/30', border: 'border-red-600/40' };
};

// Vast reports offer disk_space in GB — this is the SSH/instance disk Steam installs onto.
const formatHostStorage = (specs = {}) => {
  const raw = Number(specs.disk_space);
  if (!Number.isFinite(raw) || raw <= 0) return { label: 'N/A', gb: 0, detail: '' };
  const label = raw >= 1000 ? `${(raw / 1000).toFixed(1)} TB` : `${Math.round(raw)} GB`;
  const name = String(specs.disk_name || '').trim();
  const kind = /nvme/i.test(name) ? 'NVMe' : /ssd/i.test(name) ? 'SSD' : /hdd|spin/i.test(name) ? 'HDD' : '';
  const detail = name && name.toLowerCase() !== 'unknown' ? name : '';
  return { label: kind ? `${label} ${kind}` : label, gb: raw, detail };
};

const formatMbps = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(1)} Gbps`;
  return `${Math.round(n)} Mbps`;
};

const usd = (n, digits = 3) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toFixed(digits)}`;
};

const mkd = (n, digits = 2) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${v.toFixed(digits)} MKD`;
};

const HOURS_PER_MONTH = 30 * 24;

const MIN_GAME_DISK_GB = 100;
const MIN_LAB_DISK_GB = 50;
const DISK_STEPS = [250, 500, 750, 1000];

const minDiskGb = (specs = {}, instanceMode = 'game') => {
  const host = Math.round(Number(specs.disk_space) || 0);
  if (instanceMode === 'lab') return MIN_LAB_DISK_GB;
  if (host > 0) return Math.min(host, MIN_GAME_DISK_GB);
  return MIN_GAME_DISK_GB;
};

const diskOptionsFor = (specs = {}, instanceMode = 'game') => {
  const host = Math.round(Number(specs.disk_space) || 0);
  const floor = minDiskGb(specs, instanceMode);
  const max = host > 0 ? host : DISK_STEPS[DISK_STEPS.length - 1];
  const opts = DISK_STEPS.filter((gb) => gb >= floor && gb <= max);
  if (max < 1000 && max >= floor && !opts.includes(max)) opts.push(max);
  if (!opts.length) opts.push(Math.max(floor, max > 0 ? max : floor));
  return [...new Set(opts)].sort((a, b) => a - b);
};

const defaultDiskGb = (specs = {}, instanceMode = 'game') => {
  if (instanceMode === 'lab') return MIN_LAB_DISK_GB;
  return diskOptionsFor(specs, instanceMode)[0];
};

const clampDiskGb = (raw, specs = {}, instanceMode = 'game') => {
  const host = Math.max(0, Math.round(Number(specs.disk_space) || 0));
  const floor = minDiskGb(specs, instanceMode);
  let n = Math.round(Number(raw));
  if (!Number.isFinite(n) || n <= 0) n = defaultDiskGb(specs, instanceMode);
  if (host > 0) n = Math.min(n, host);
  return Math.max(floor, n);
};

const formatTflops = (specs = {}) => {
  const n = Number(specs.tflops);
  if (!Number.isFinite(n) || n <= 0) return { label: '—', title: 'TFLOPS was not published for this offer.' };
  return {
    label: `${n >= 100 ? Math.round(n) : n.toFixed(1)} TFLOPS`,
    title: 'Theoretical GPU compute (FP32, all GPUs on the offer). Not the same as game FPS.',
  };
};

// Customer-facing Vast bill: GPU (dph_base) + the disk the customer picks.
const priceBreakdown = (specs = {}, fallbackHourly = 0, chosenGb = 0) => {
  const gpuHr = Number(specs.usd_gpu_per_hour);
  const storageMo = Number(specs.storage_cost_per_gb_month);
  const advertisedGb = Number(specs.disk_space) || Number(specs.billed_disk_gb) || 0;
  const billedGb = chosenGb > 0 ? chosenGb : advertisedGb;
  let diskHr = Number(specs.usd_storage_per_hour);
  if (Number.isFinite(storageMo) && storageMo > 0 && billedGb > 0) {
    diskHr = (storageMo / HOURS_PER_MONTH) * billedGb;
  } else if (advertisedGb > 0 && billedGb > 0 && Number.isFinite(diskHr) && diskHr > 0) {
    diskHr = diskHr * (billedGb / advertisedGb);
  }
  const hasSplit = Number.isFinite(gpuHr) && Number.isFinite(diskHr);
  const totalHr = hasSplit
    ? gpuHr + diskHr
    : Number(specs.usd_total_per_hour ?? specs.usd_price_per_hour ?? fallbackHourly) || 0;
  return {
    totalHr,
    gpuHr: hasSplit ? gpuHr : null,
    diskHr: hasSplit ? diskHr : null,
    min: totalHr / 60,
    day: totalHr * 24,
    month: totalHr * 24 * 30,
    gpuDay: hasSplit ? gpuHr * 24 : null,
    diskDay: hasSplit ? diskHr * 24 : null,
    gpuMonth: hasSplit ? gpuHr * 24 * 30 : null,
    diskMonth: hasSplit ? diskHr * 24 * 30 : null,
    billedGb,
  };
};

const hoursLeftLabel = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return '0m';
  const totalMin = Math.round(h * 60);
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hrs <= 0) return `${mins}m`;
  return mins ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const GPUSelection = ({
  availableGPUs = [],
  onLaunchSession,
  isLoading = false,
  isLaunching = false,
  launchingGPUId = null,
  onRefresh = null,
  region = 'any',
  userRegion = 'EU',
  sortBy = 'price',
  instanceMode = 'game',
  walletMkd = 0,
  creditGated = true,
  familyQuad = false,
  onRegionChange = null,
  onUserRegionChange = null,
  onSortByChange = null,
}) => {
  const [exchangeRate, setExchangeRate] = useState(null);
  const [diskByGpu, setDiskByGpu] = useState({});
  // Handle launching session directly (no pre-selection, no selection indicators)
  const handleLaunchSession = (gpu, diskGb, usdPerHour) => {
    console.log('🎮 GPUSelection: Launch button clicked for GPU:', gpu.id, gpu.gpu_type, diskGb);
    
    if (onLaunchSession) {
      onLaunchSession({ ...gpu, disk_space: diskGb, quoted_usd_per_hour: usdPerHour });
    }
  };

  // Fetch exchange rate on component mount
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const rateData = await exchangeRateAPI.getRate();
        setExchangeRate(rateData.usd_to_mkd_rate);
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
        setExchangeRate(57.5);
      }
    };

    fetchExchangeRate();
    
    // Update exchange rate every 5 minutes
    const interval = setInterval(fetchExchangeRate, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // No automatic GPU selection - users must manually select


  // Backend already applies region filter, ping estimate and sort order — we
  // trust the server response and only fall back to a client-side price sort
  // if the server returned no sort hint.
  const filteredGPUs = [...availableGPUs];
  if (sortBy === 'price' || !sortBy) {
    filteredGPUs.sort((a, b) => {
      const priceA = a.specs?.usd_price_per_hour ?? (a.price_per_hour / (exchangeRate || 57.5));
      const priceB = b.specs?.usd_price_per_hour ?? (b.price_per_hour / (exchangeRate || 57.5));
      return priceA - priceB;
    });
  }

  // No GPU pre-selection - customers choose manually

  const getGPUIcon = (gpuType) => {
    if (gpuType.includes('RTX 3090')) return '🎮';
    if (gpuType.includes('RTX 3080')) return '⚡';
    if (gpuType.includes('RTX 4070')) return '🚀';
    if (gpuType.includes('RTX 4080')) return '💎';
    if (gpuType.includes('RTX 4090')) return '👑';
    return '🖥️';
  };

  // Removed getGPUColor function - no special styling for any GPU

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-800 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* GPU List Status */}
      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-green-300 text-sm font-medium">GPU List Status</span>
          </div>
          <div className="text-right">
            <div className="text-green-400 font-bold">{availableGPUs.length} GPUs Available</div>
            <div className="text-green-500 text-xs">
              Auto-refresh every 30s
            </div>
          </div>
        </div>
      </div>


      {/* Region / sort controls */}
      <div className="bg-slate-900/70 border border-indigo-500/25 rounded-xl p-3.5 flex flex-col md:flex-row md:items-end gap-3">
        <FilterSelect
          label="Region"
          value={region}
          onChange={(e) => onRegionChange && onRegionChange(e.target.value)}
        >
          {REGION_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </FilterSelect>
        <FilterSelect
          label="Measured from (ping)"
          value={userRegion}
          onChange={(e) => onUserRegionChange && onUserRegionChange(e.target.value)}
        >
          {REGION_OPTIONS.filter(r => r.value !== 'any').map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </FilterSelect>
        <FilterSelect
          label="Sort by"
          value={sortBy}
          onChange={(e) => onSortByChange && onSortByChange(e.target.value)}
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </FilterSelect>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium py-2.5 px-5 rounded-xl"
          >
            Refresh
          </button>
        )}
      </div>

      {familyQuad && instanceMode === 'game' && (
        <div className="bg-sky-950/40 border border-sky-500/30 rounded-lg px-3 py-2 text-sm text-sky-100 leading-relaxed">
          Family plan: one GPU for all four seats. List is limited to 12 GB
          4070-class and up that can encode four streams. Stream stays Full HD.
        </div>
      )}
      {!familyQuad && instanceMode === 'game' && (
        <div className="bg-sky-950/40 border border-sky-500/30 rounded-lg px-3 py-2 text-sm text-sky-100 leading-relaxed">
          Solo Game lists 8 GB Full HD class and above. Weaker cards are hidden.
        </div>
      )}
      {instanceMode === 'game' && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg px-3 py-2 text-sm text-amber-100 leading-relaxed">
          {filteredGPUs.length <= 2
            ? `Only ${filteredGPUs.length} GPU machine${filteredGPUs.length === 1 ? '' : 's'} can run Game right now. This list changes as hosts rent and unlist machines.`
            : 'Game only lists full VM machines that can run the browser stream. Container-only offers are hidden on purpose.'}
        </div>
      )}

      {/* GPU Count Info */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-gray-400 text-sm">
          Showing {filteredGPUs.length} GPUs — sorted by {(SORT_OPTIONS.find(s => s.value === sortBy) || SORT_OPTIONS[0]).label.toLowerCase()}
        </span>
        <span className="text-gray-400 text-sm">
          Ping is estimated from {(REGION_OPTIONS.find(r => r.value === userRegion) || {}).label || userRegion}
        </span>
      </div>


      {/* GPU Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredGPUs.map((gpu) => {
          const isThisGPULoading = isLaunching && launchingGPUId === gpu.id;
          
          // Show loading tile for launching GPU
          if (isThisGPULoading) {
            return (
              <div
                key={`loading-${gpu.id}`}
                className="p-4 rounded-lg border-2 border-orange-500 bg-orange-900/20 ring-2 ring-orange-500/50"
              >
                <div className="flex items-center justify-center mb-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-400 mr-3"></div>
                  <h3 className="text-orange-400 font-semibold text-lg">Loading...</h3>
                </div>
                
                <div className="text-center">
                  <div className="text-orange-300 text-sm mb-2">
                    Please Wait
                  </div>
                  <div className="text-orange-400 text-xs mb-4">
                    Launching {gpu.gpu_type} GPU Session
                  </div>
                  
                  <div className="space-y-2 text-xs text-orange-300">
                    <div className="flex items-center justify-between">
                      <span>GPU:</span>
                      <span className="text-orange-200">{gpu.gpu_type}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Price:</span>
                      <span className="text-orange-200">
                        {Number(gpu.specs?.usd_price_per_hour)
                          ? `$${Number(gpu.specs.usd_price_per_hour).toFixed(3)}/h`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Status:</span>
                      <span className="text-orange-200">Initializing...</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-2 bg-orange-800/30 rounded-lg">
                    <div className="text-orange-200 text-xs text-center">
                      Your gaming session is being prepared...
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          const countryCode = gpu.specs?.country_code || '';
          const detectedCC = gpu.specs?.detected_country_code || '';
          const geoVerified = gpu.specs?.geo_verified; // true | false | null | undefined
          const flag = flagFromCountryCode(countryCode);
          const detFlag = flagFromCountryCode(detectedCC);
          const measured = gpu.specs?.measured_rtt_ms;
          const estimated = gpu.specs?.estimated_latency_ms;
          const rtt = measured != null ? measured : estimated;
          const pc = pingColor(rtt);
          const rttIsMeasured = measured != null;
          const storage = formatHostStorage(gpu.specs);
          const storageTight = storage.gb > 0 && storage.gb < 80;
          const diskChoices = diskOptionsFor(gpu.specs, instanceMode);
          const diskDraft = Object.prototype.hasOwnProperty.call(diskByGpu, gpu.id)
            ? Number(diskByGpu[gpu.id])
            : defaultDiskGb(gpu.specs, instanceMode);
          const chosenGb = diskChoices.includes(diskDraft)
            ? diskDraft
            : clampDiskGb(diskDraft, gpu.specs, instanceMode);
          const price = priceBreakdown(gpu.specs, gpu.price_per_hour, chosenGb);
          const perf = formatTflops(gpu.specs);
          const netDown = formatMbps(gpu.specs?.inet_down);
          const netUp = formatMbps(gpu.specs?.inet_up);
          const ports = Number(gpu.specs?.direct_port_count || gpu.specs?.network_ports) || 0;
          const billedGb = price.billedGb || chosenGb;
          const usdHr = price.totalHr;
          const mkdHr = (Number(gpu.specs?.exchange_rate) || exchangeRate)
            ? usdHr * (Number(gpu.specs?.exchange_rate) || exchangeRate)
            : gpu.price_per_hour;

          return (
            <div
              key={gpu.id}
              className="p-4 rounded-lg border-2 transition-colors duration-200 border-gray-600 bg-gray-800 hover:border-gray-500"
            >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getGPUIcon(gpu.gpu_type)}</span>
                <h3 className="text-white font-semibold text-sm">{gpu.gpu_type}</h3>
              </div>
              <div className="text-right">
                <div className="inline-flex items-center px-2 py-0.5 rounded-md bg-black/70 text-white font-bold text-sm">
                  {usd(usdHr, 3)}/h
                </div>
                <div className="text-gray-400 text-[11px] mt-1 leading-tight">
                  {Number.isFinite(mkdHr) ? `${mkd(mkdHr)}/h` : '—'}
                </div>
                <div className="text-gray-400 text-[11px] leading-tight">
                  {usd(usdHr / 60, 4)}/min · {usd(usdHr * 24, 2)}/day
                </div>
              </div>
            </div>

            {/* Location + verify + ping badges (Level 2 & 4) */}
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-gray-600 bg-gray-700/50 text-gray-200">
                {flag ? <span className="text-base leading-none">{flag}</span> : null}
                <span>{countryCode || (gpu.location || 'Unknown')}</span>
              </span>

              {geoVerified === true && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-emerald-600/40 bg-emerald-900/30 text-emerald-300"
                      title="IP geolocation matches the host's listed country">
                  <ShieldCheck className="w-3 h-3" />
                  Verified
                </span>
              )}
              {geoVerified === false && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-red-600/40 bg-red-900/30 text-red-300"
                      title={`IP resolves to ${detectedCC || 'a different country'} — Steam etc. will see this location, not the listed one`}>
                  <ShieldAlert className="w-3 h-3" />
                  {detFlag ? <span className="text-base leading-none">{detFlag}</span> : null}
                  IP {detectedCC || 'differs'}
                </span>
              )}

              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${pc.bg} ${pc.border} ${pc.text}`}
                    title={rttIsMeasured
                      ? `Measured RTT (TCP handshake from platform in EU): ${Math.round(rtt)} ms`
                      : `Estimated RTT from ${userRegion}: ~${rtt != null ? Math.round(rtt) : '?'} ms`}>
                {rttIsMeasured ? <Activity className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
                {rtt != null ? `${rttIsMeasured ? '' : '~'}${Math.round(rtt)} ms` : '~ ? ms'}
                {rttIsMeasured && <span className="opacity-70">•</span>}
              </span>
            </div>

            <div className="space-y-2 text-xs text-gray-300">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Performance
                </span>
                <span className="text-white" title={perf.title}>{perf.label}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Cpu className="w-3 h-3" />
                  VRAM
                </span>
                <span className="text-white">{gpu.specs?.gpu_ram || 'N/A'} GB</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  CUDA
                </span>
                <span className="text-white">{gpu.specs?.cuda_max_good || 'N/A'}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">CPU</span>
                <span className="text-white text-xs">{gpu.specs?.cpu_cores || 'N/A'} cores</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">RAM</span>
                <span className="text-white text-xs">{gpu.specs?.cpu_ram ? (gpu.specs.cpu_ram / 1000).toFixed(1) : 'N/A'} GB</span>
              </div>

              <div className="rounded-lg bg-indigo-950/50 border border-indigo-400/40 px-3 py-2.5 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-indigo-100 font-semibold text-sm">
                    <HardDrive className="w-4 h-4" />
                    Storage
                  </span>
                  <span className={storageTight ? 'text-amber-300 text-xs' : 'text-indigo-200/70 text-xs'}>
                    host max {storage.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {diskChoices.map((gb) => {
                    const on = Number(chosenGb) === Number(gb);
                    return (
                      <button
                        key={gb}
                        type="button"
                        onClick={() => setDiskByGpu((prev) => ({ ...prev, [gpu.id]: gb }))}
                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                          on
                            ? 'bg-indigo-500 text-white border-indigo-300'
                            : 'bg-slate-950 text-indigo-100 border-indigo-400/50 hover:border-indigo-300'
                        }`}
                      >
                        {gb} GB
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-indigo-200/70 leading-tight">
                  Price below is GPU + this disk. Default is 250 GB when the host allows it.
                </p>
              </div>

              {(price.gpuHr != null || price.diskHr != null) && (
                <div className="rounded-md bg-gray-900/60 border border-gray-700 px-2 py-1.5 space-y-1">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-400">GPU</span>
                    <span className="text-gray-200">
                      {usd(price.gpuHr || 0, 3)}/h · {usd(price.gpuDay || 0, 2)}/day · {usd(price.gpuMonth || 0, 2)}/mo
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-400">Disk {billedGb} GB</span>
                    <span className="text-gray-200">
                      {usd(price.diskHr || 0, 3)}/h · {usd(price.diskDay || 0, 2)}/day · {usd(price.diskMonth || 0, 2)}/mo
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] pt-0.5 border-t border-gray-700">
                    <span className="text-gray-400">All resources / min</span>
                    <span className="text-white font-medium">{usd(price.min || 0, 4)}</span>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-gray-400">
                  <Wifi className="w-3 h-3" />
                  Network
                </span>
                <span className="text-white text-xs inline-flex items-center gap-2">
                  {netDown ? (
                    <span className="inline-flex items-center gap-0.5" title="Download">
                      <ArrowDown className="w-3 h-3 text-sky-400" />{netDown}
                    </span>
                  ) : null}
                  {netUp ? (
                    <span className="inline-flex items-center gap-0.5" title="Upload">
                      <ArrowUp className="w-3 h-3 text-emerald-400" />{netUp}
                    </span>
                  ) : null}
                  {!netDown && !netUp ? 'N/A' : null}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-400">Public ports</span>
                <span className="text-white text-xs">
                  {ports > 0 ? `${ports} mapped` : 'N/A'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Location</span>
                <span className="text-white text-xs">{gpu.location || 'N/A'}</span>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-600 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">This GPU</span>
                <span className="text-white font-medium">
                  {Number.isFinite(usdHr) ? `${usd(usdHr, 3)}/h` : '—'}
                  {Number.isFinite(mkdHr) ? ` · ${mkd(mkdHr)}/h` : ''}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Your credit</span>
                <span className="text-indigo-200">
                  {walletMkd > 0 && mkdHr > 0
                    ? `≈ ${hoursLeftLabel(walletMkd / mkdHr)} left`
                    : 'Top up on Profile'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleLaunchSession(gpu, clampDiskGb(billedGb, gpu.specs, instanceMode), price.totalHr)}
                disabled={isLaunching || (creditGated && mkdHr > 0 && walletMkd / mkdHr < 1)}
                className={`w-full text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors ${
                  isLaunching || (creditGated && mkdHr > 0 && walletMkd / mkdHr < 1)
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isLaunching
                  ? 'Please Wait...'
                  : creditGated && mkdHr > 0 && walletMkd / mkdHr < 1
                    ? 'Need 1 hour of credit'
                    : 'Launch GPU'}
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {filteredGPUs.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">No GPUs Available</div>
          <div className="text-gray-500 text-sm">
            Try adjusting your filters or maximum price
          </div>
        </div>
      )}
    </div>
  );
};

export default GPUSelection;
